import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  DndContext, 
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

// Design
import { Star, ChevronRight } from 'lucide-react';
import './globals.css'

// Translations
import { useTranslation } from 'react-i18next';
import './i18n/i18n';
import TranslatedText from './i18n/TranslatedText';

// Components
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button, ShareButton, HomeButton, TrainingButton, CancelButton } from './components/ui/button';
import { Tooltip } from './components/ui/tooltip';
import InstallAppModal from './components/InstallAppModal';
import TrainingModeModal from './components/TrainingModeModal';
import CancelGameModal from './components/CancelGameModal';
import { Highscores, SubmissionOverlay } from './components/Highscores';
import { PersonalBestDisplay } from './components/PersonalBestDisplay';
import GoatCounter from "./components/analytics/GoatCounter";
import GameAnalytics from "./components/analytics/GameAnalytics";
import { DraggableProduct, DragPreview, DroppableCustomer } from './components/dnd/DragAndDrop';

// Utils
import { ScoreStatus, saveScore } from './utils/scores';
import { savePersonalBest, getPersonalBest, isRecordRun } from './utils/personalBest';
import { updateGameState, loadGameState, clearGameState } from './utils/gameState';
import { toggleScrollLock } from './utils/scrollLock';

// Hooks
import { useTimer } from './hooks/useTimer';
import useTooltipState from './hooks/useTooltipState';



// App States
const AppState = {
  WELCOME: 'WELCOME',
  PLAYING: 'PLAYING', 
  DAY_BREAK: 'DAY_BREAK',
  GAME_OVER: 'GAME_OVER'
};

// Game constants
const STARTING_BUDGET = 100;
const SPD = 180; // Seconds per day
const INITIAL_RATING = 50;
const INITIAL_TIME = 4 * (SPD/24);
const PRODUCTS = {
  brot: {t: 14, b: 4, p: 3, c: 10, e: '🥖', o: 2},  
  breze: {t: 9, b: 10, p: 1, c: 8, e: '🥨', o: 1},
  kuchen: {t: 20, b: 1, p: 12, c: 9, e: '🎂', o: 6}  
};

// Initial Products
const initialProducts = {
  brot: { amount: 0, inProduction: 0, productionTimes: [] },
  kuchen: { amount: 0, inProduction: 0, productionTimes: [] },
  breze: { amount: 0, inProduction: 0, productionTimes: [] }
};

// Initial Stats
const initialDailyStats = {
  satisfiedCustomers: 0,
  lostCustomers: 0,
  productionCosts: 0,
  revenue: 0,
  soldProducts: {
    brot: 0,
    kuchen: 0,
    breze: 0
  },
  wastedProducts: {
    brot: 0,
    kuchen: 0,
    breze: 0
  },
  initialRating: INITIAL_RATING,
  initialBudget: STARTING_BUDGET
};


const BakeryGame = () => {
  const { t, i18n } = useTranslation();
  
  // Language Detection
  useEffect(() => {
    const lang = i18n.language?.split('-')[0]; // Handle cases like 'de-DE'
    if (lang !== i18n.language) {
      i18n.changeLanguage(lang);
    }
  }, [i18n]);

  // Game state
  const [appState, setAppState] = useState(AppState.WELCOME);
  const [trainingMode, setTrainingMode] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [dayClosed, setDayClosed] = useState(false);
  const [money, setMoney] = useState(STARTING_BUDGET);
  const [time, setTime] = useState(INITIAL_TIME);
  const [day, setDay] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [rating, setRating] = useState(INITIAL_RATING);
  const [products, setProducts] = useState(initialProducts);
  const [dailyStats, setDailyStats] = useState(initialDailyStats);
  const [loadStats, setLoadStats] = useState(null);
  const [finalStats, setFinalStats] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [playerName, setPlayerName] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [scoreRefreshTrigger, setScoreRefreshTrigger] = useState(0);
  const [personalBest, setPersonalBest] = useState(getPersonalBest());
  const [isRecordRunState, setIsRecordRunState] = useState(false);
  const [isTrainingModalOpen, setIsTrainingModalOpen] = useState(false);
  const [cancelModalConfig, setCancelModalConfig] = useState({
    isOpen: false,
    onConfirm: null
  });
  const lastSpawnTimeRef = useRef(0);
  const emptyQueueTimeRef = useRef(0);
  const processingRef = React.useRef(new Set());


  // Time
  const vh = Math.floor((time % SPD) / (SPD / 24));
  const dt = (() => {
    let hours = vh;
    const minutes = Math.floor((time % (SPD/24)) / (SPD/24/60));
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  })();
  const isOpen = vh >= 6 && vh <= 18;
  const isBaking = vh >= 4 && vh <= 18;


  // Tooltips
  const [bakingTooltip, showBakingTooltip, dismissBakingTooltip] = useTooltipState('baking', 0, false);
  const [servingTooltip, showServingTooltip, dismissServingTooltip] = useTooltipState('serving', 0, false);
  const [ratingTooltip, showRatingTooltip, dismissRatingTooltip] = useTooltipState('rating', 6000, false);



  // FUNCTIONS
  // startNewDay Function
  const startNewDay = useCallback((scenario = 'newGame', gameState = null) => {
    setTime(INITIAL_TIME);
    setActiveId(null);
    setDayClosed(false);
    setProducts({ ...initialProducts });
    setCustomers([]);
    setFinalStats(null);
    setSubmissionSuccess(false);
    setCancelModalConfig({ isOpen: false, onConfirm: null });
    emptyQueueTimeRef.current = 0;
    lastSpawnTimeRef.current = 0;
    let newLoadStats = null;

    switch (scenario) {
      case 'newGame':
        newLoadStats = {
          day: 1,
          money: STARTING_BUDGET,
          rating: INITIAL_RATING,
          stats: { totalRevenue: 0, totalCustomers: 0 },
          dayComplete: false,
          trainingMode: false
        };
        setLoadStats(newLoadStats);
        updateGameState(newLoadStats);
        setDay(1);
        setMoney(STARTING_BUDGET);
        setRating(INITIAL_RATING);
        setTrainingMode(false);
        setDailyStats({ ...initialDailyStats });
        GameAnalytics.trackDayStart({ day: 1, training: false });
        break;

      case 'continue':
        newLoadStats = {
          ...loadStats,          
          day: loadStats.day + 1,
          dayComplete: false    
        };  
        setLoadStats(newLoadStats);
        updateGameState(newLoadStats);
        setDay(loadStats.day + 1);
        setDailyStats(prev => ({
          ...initialDailyStats,
          initialRating: loadStats.rating,
          initialBudget: loadStats.money
        }));
        GameAnalytics.trackDayStart({ day: loadStats.day + 1, training: loadStats.trainingMode });
        break;

      case 'retryInTrainingMode':
        newLoadStats = {
          ...loadStats,
          dayComplete: false,
          trainingMode: true
        };
        setLoadStats(newLoadStats);
        updateGameState(newLoadStats);
        setDay(loadStats.day);
        setMoney(loadStats.money);
        setRating(loadStats.rating);
        setTrainingMode(true);
        setDailyStats(prev => ({
          ...initialDailyStats,
          initialRating: loadStats.rating,
          initialBudget: loadStats.money
        }));
        GameAnalytics.trackDayStart({ day: loadStats.day + 1, training: true});
      break;

      case 'trainingMode': 
        if (gameState) {
          setLoadStats(gameState);
          updateGameState(gameState);
          setDay(gameState.day);
          setMoney(gameState.money);
          setRating(gameState.rating);
          setTrainingMode(true);
          setDailyStats(prev => ({
            ...initialDailyStats,
            initialRating: gameState.rating,
            initialBudget: gameState.money
          }));
          GameAnalytics.trackDayStart({ day: gameState.day, training: true });
        }
        break;

      default:
        return;
    }

    setAppState(AppState.PLAYING);

  }, [
    setTime,
    setActiveId,
    setDayClosed,
    setProducts,
    setCustomers,
    setFinalStats,
    setSubmissionSuccess,
    setCancelModalConfig,
    setDay,
    setMoney,
    setRating,
    setTrainingMode,
    setDailyStats,
    setAppState,
    loadStats
  ]);


  // startProduction Function
  const startProduction = (p) => {
    if (money >= PRODUCTS[p].c && isBaking && products[p].inProduction < PRODUCTS[p].o) {
      setTimerPaused(false);
      
      // Automatically dismiss baking tooltip on first production
      if (bakingTooltip.visible) {
        dismissBakingTooltip();
      }
      
      setMoney(prev => prev - PRODUCTS[p].c);
      setDailyStats(prev => ({
        ...prev,
        productionCosts: prev.productionCosts + PRODUCTS[p].c
      }));
      setProducts(prev => ({
        ...prev,
        [p]: {
          ...prev[p],
          inProduction: prev[p].inProduction + 1,
          productionTimes: [...prev[p].productionTimes, PRODUCTS[p].t]
        }
      }));
    }
  };

  // Customer Frequency Calculation
  const cf = useCallback(() => {
    if (!isOpen) return 0;
      let baseFrequency;
      if (vh >= 7 && vh < 9)        { baseFrequency = 7; }  // Rush Hour 1
      else if (vh >= 15 && vh < 17) { baseFrequency = 7; }  // Rush Hour 2
      else                          { baseFrequency = 5; }  // Normal Hours
      
      let dayMultiplier;
      if (day === 1)  dayMultiplier = 1;
      if (day <= 3)   dayMultiplier = 1 + (day * 0.3);           // 1.3 on day 2, 1.45 on day 3
      if (day > 3)    dayMultiplier = 1.9 + ((day - 3) * 0.2);  // 1.7 on day 4, 1.95 on day 5, 2.2 on day 6, etc.
    
      const ratingMultiplier = 0.50 + ((rating / 10) * 0.1);     // 1.0 at 50 rating, 0.55 at 0 rating

    return Math.round(baseFrequency * dayMultiplier * ratingMultiplier);
  }, [isOpen, vh, rating, day]);

  // maxQueueSize
  const getMaxQueueSize = useCallback((day) => {
    if (day >= 25) return 32;
    if (day >= 22) return 28;
    if (day >= 19) return 24;
    if (day >= 16) return 22;
    if (day >= 13) return 20;
    if (day >= 10) return 18;
    if (day >= 8) return 16;
    if (day >= 7) return 14;
    if (day >= 5) return 12;
    if (day >= 3) return 8;
    if (day >= 2) return 6;
    return 4;
  }, []);
  
    // maxSpawnCount
  const getMaxSpawnCount = useCallback((day) => {
    if (day >= 18) return 7;
    if (day >= 15) return 6;
    if (day >= 11) return 5;
    if (day >= 6) return 4;
    if (day >= 4) return 3;
    if (day >= 2) return 2;
    return 1;
  }, []);

  // Patience
  const calculatePatience = useCallback((day) => {
    if (day >= 20) {
      return Math.floor(Math.random() * 8) + 5; // 6-13 seconds
    } else if (day >= 17) {
      return Math.floor(Math.random() * 8) + 6; // 6-13 seconds
    } else if (day >= 14) {
      return Math.floor(Math.random() * 8) + 7; // 7-14 seconds
    } else if (day >= 12) {
      return Math.floor(Math.random() * 8) + 8; // 8-15 seconds
    } else if (day >= 9) {
      return Math.floor(Math.random() * 8) + 9; // 9-16 seconds
    } else if (day >= 3) {
      return Math.floor(Math.random() * 8) + 10; // 10-17 seconds
    } else if (day >= 2) {
      return Math.floor(Math.random() * 8) + 15; // 15-22 seconds
    } else {
      return Math.floor(Math.random() * 8) + 20; // 20-27 seconds
    }
  }, []);


  // handleServeCustomer Function
  const handleServeCustomer = (customerId, productType) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer && customer.wants === productType && products[productType].amount > 0) {
      // Immediately update product amount, money, and stats
      setProducts(p => ({
        ...p,
        [productType]: {
          ...p[productType],
          amount: p[productType].amount - 1
        }
      }));
      setMoney(p => p + PRODUCTS[productType].p);
      setDailyStats(p => ({
        ...p,
        revenue: p.revenue + PRODUCTS[productType].p,
        satisfiedCustomers: p.satisfiedCustomers + 1,
        soldProducts: {
          ...p.soldProducts,
          [productType]: p.soldProducts[productType] + 1
        }
      }));
      setRating(prev => Math.min(50, prev + (customer.patience > 12 ? 3 : (customer.patience > 6 ? 2 : 1))));
      // Mark customer as earned and remove after animation
      setCustomers(prev => prev.map(c => 
        c.id === customerId 
          ? {...c, earned: PRODUCTS[productType].p}
          : c
      ));

      if (servingTooltip.visible) {
          dismissServingTooltip();
        }

      setTimeout(() => {
        setCustomers(p => p.filter(c => c.id !== customerId));
      }, 1000);

    }
  };

  // Customers leaving Function
  const processLeavingCustomers = useCallback((customers) => {
    const leavingCustomers = customers.filter(c => c.patience < 0 && !c.ratingLost && !c.earned);
    
    if (leavingCustomers.length > 0) {
      const batchKey = leavingCustomers.map(c => c.id).join(',');
      
      if (!processingRef.current.has(batchKey)) {
        processingRef.current.add(batchKey);

        // Update both daily stats and total game stats
        setDailyStats(prev => ({
          ...prev,
          lostCustomers: prev.lostCustomers + leavingCustomers.length
        }));

        setRating(prev => {
          const deduction = Math.min(prev, 5 * leavingCustomers.length);
          return Math.max(0, prev - deduction);
        });

        // Mark customers as rating lost and remove them after animation
        const updatedCustomers = customers.map(c => 
          leavingCustomers.find(lc => c.id === lc.id)
            ? { ...c, ratingLost: true }
            : c
        );

        setTimeout(() => {
          setCustomers(prev => prev.filter(c => !leavingCustomers.find(lc => c.id === lc.id)));
          processingRef.current.delete(batchKey);
        }, 0);

        return updatedCustomers;
      }
    }
    
    return customers.map(c => 
      c.ratingLost || c.earned ? c : { ...c, patience: c.patience - 1 }
    );
  }, []);

  // Drag and Drop Functions
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over) return;

    const draggedProductType = active.data.current.productType;
    const customerId = over.id;
    
    const customer = customers.find(c => c.id === customerId);
    const isValidDrop = customer && 
      customer.wants === draggedProductType && 
      products[draggedProductType].amount > 0;

    if (isValidDrop) {
      setCustomers(prev => prev.map(c => 
        c.id === customerId 
          ? {...c, earned: PRODUCTS[draggedProductType].p}
          : c
      ));
      handleServeCustomer(customerId, draggedProductType);
    }
  };
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 1,
      tolerance: 5,
      delay: 0
    },
  });
  
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 1, 
      tolerance: 5
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  // closeDay Function
  const closeDay = useCallback(() => {
    if (appState !== AppState.PLAYING || dayClosed) {
      return;
    }
    setDayClosed(true);

    const wastedProducts = {...products};
    const dayStats = {
      ...dailyStats,
      wastedProducts: {
        brot: wastedProducts.brot.amount,
        kuchen: wastedProducts.kuchen.amount,
        breze: wastedProducts.breze.amount
      },
      ratingChange: (rating - dailyStats.initialRating)/10,
      budgetChange: money - dailyStats.initialBudget
    };

    const newLoadStats = {
      day: day,
      money: money,
      rating: rating,
      stats: {
        totalRevenue: (loadStats.stats?.totalRevenue || 0) + dayStats.revenue,
        totalCustomers: (loadStats.stats?.totalCustomers || 0) + dayStats.satisfiedCustomers
      },
      dayComplete: true,
      trainingMode: trainingMode
    };
    setLoadStats(newLoadStats);
    updateGameState(newLoadStats);
    setDailyStats(dayStats);

    const currentStats = {
      daysSurvived: day,
      moneyEarned: money,
    };
    setIsRecordRunState(isRecordRun(currentStats));

    setAppState(AppState.DAY_BREAK);  

    GameAnalytics.trackDayEnd({ day: day, training: trainingMode });
    
  }, [appState, dayClosed, products, dailyStats, loadStats, rating, money, day, trainingMode]);


  const backToHome = (source) => {
    if (!dayClosed) {
      clearGameState();
      setLoadStats(null);
      setDay(1);
      setRating(INITIAL_RATING);
      setMoney(STARTING_BUDGET);
      setTrainingMode(false);
    }
    setAppState(AppState.WELCOME);
    GameAnalytics.trackHomeButton(source);
  }


  const handleSubmitScore = async () => {
    if (!playerName.trim()) return;
    
    setSubmissionInProgress(true);

    const result = await saveScore({
      playerName: playerName.trim(),
      daysSurvived: finalStats.daysSurvived,
      moneyEarned: finalStats.moneyEarned,
      servedCustomers: finalStats.servedCustomers,
      totalRevenue: finalStats.totalRevenue
    });

//    console.log("Save Score Result:", result);

  
    switch (result.status) {
      case ScoreStatus.SUCCESS:
        setScoreRefreshTrigger(prev => prev + 1);
        setSubmissionStatus('success');
        setSubmissionSuccess(true);
        break;
      case ScoreStatus.SCORE_UPDATED:
        setScoreRefreshTrigger(prev => prev + 1);
        setSubmissionStatus('score-updated');
        setSubmissionSuccess(true);
        break;
      case ScoreStatus.LOWER_SCORE:
        setSubmissionStatus('lower-score');
        break;
      case ScoreStatus.INVALID_NAME_LENGTH:
      case ScoreStatus.INVALID_DAYS:
      case ScoreStatus.INVALID_MONEY:
      case ScoreStatus.INVALID_METRICS:
      case ScoreStatus.ERROR:
      default:
        setSubmissionStatus('error');
    }

    setSubmissionInProgress(false);

    setTimeout(() => {
      setSubmissionStatus(null);
//      console.log("Submission Status Reset");
    }, 3500);
  };
  
  


  // EFFECTS
  
  // Customer generation
  useEffect(() => {
    if (appState !== AppState.PLAYING || !isOpen) return;
      
    const currentTime = time;
    const timeSinceSpawn = currentTime - lastSpawnTimeRef.current;
    
    if (timeSinceSpawn >= 1) {
      if (customers.length === 0) {
        emptyQueueTimeRef.current += 1;
      } else {
        emptyQueueTimeRef.current = 0;
      }
  
      const baseFrequency = cf();
      const cooldownComplete = emptyQueueTimeRef.current >= 5;
      const shouldSpawn = (customers.length === 0 && cooldownComplete) || 
                         ((Math.random() * 100) < baseFrequency);
      
      if (shouldSpawn) {
        const maxQueueSize = getMaxQueueSize(day);
        const maxCustomers = Math.min(
          getMaxSpawnCount(day),
          Math.max(0, maxQueueSize - customers.length)
        );
        
        if (maxCustomers > 0) {
          // Get all occupied positions first
          const occupiedPositions = new Set(customers.map(c => c.position));
          
          // Find all available positions up to maxQueueSize
          const availablePositions = Array.from({ length: maxQueueSize })
            .map((_, idx) => idx)
            .filter(pos => !occupiedPositions.has(pos));
  
          // Only spawn as many customers as we have available positions
          const spawnCount = Math.min(
            Math.floor(Math.random() * maxCustomers) + 1,
            availablePositions.length
          );
  
          if (spawnCount > 0) {
            const nc = Array(spawnCount)
              .fill(null)
              .map((_, idx) => ({
                id: Math.random(),
                wants: Object.keys(PRODUCTS)[Math.floor(Math.random() * 3)],
                patience: calculatePatience(day),
                arrivalTime: currentTime,
                position: availablePositions[idx], // Assign positions sequentially from available spots
                ratingLost: false
              }));
            
            setCustomers(prev => [...prev, ...nc]);
            lastSpawnTimeRef.current = currentTime;
            emptyQueueTimeRef.current = 0;
          }
        }
      }
    }
  }, [time, isOpen, cf, appState,
      day, customers, getMaxQueueSize, getMaxSpawnCount, calculatePatience]);


  // Customers leaving 
  useEffect(() => {
    if (appState !== AppState.PLAYING) return;
    setCustomers(prevCustomers => processLeavingCustomers(prevCustomers));
  }, [time, processLeavingCustomers, appState]);

  // Production
  useEffect(() => {
    const updateProductionTimes = () => {
      Object.entries(products).forEach(([p, i]) => {
        if (i.productionTimes.length > 0) {
          const ut = i.productionTimes.map(t => t - 1);
          const f = ut.filter(t => t <= 0).length;
          if (f > 0) {
            setProducts(prev => ({...prev, [p]: {
              amount: prev[p].amount + (PRODUCTS[p].b * f),
              inProduction: prev[p].inProduction - f,
              productionTimes: ut.filter(t => t > 0)
            }}));
          } else {
            setProducts(prev => ({...prev, [p]: {...prev[p], productionTimes: ut}}));
          }
        }
      });
    };

    updateProductionTimes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  // useEffect to Call closeDay at 18
  useEffect(() => {
    const currentVh = Math.floor((time % SPD) / (SPD / 24));
    
    if (currentVh === 18 && appState === AppState.PLAYING && !dayClosed) {
      closeDay();
    }
  }, [time, closeDay, appState, dayClosed]);


  // Game Over
  useEffect(() => {
    if (rating <= 0 && appState === AppState.PLAYING) {
      const currentDayStats = {
        revenue: dailyStats.revenue,
        customers: dailyStats.satisfiedCustomers
      };

      const finalGameStats = {
        daysSurvived: day,
        servedCustomers: (loadStats?.stats?.totalCustomers || 0) + currentDayStats.customers,
        totalRevenue: (loadStats?.stats?.totalRevenue || 0) + currentDayStats.revenue,
        moneyEarned: money
      };
      
      setFinalStats(finalGameStats);

      const currentStats = {
        daysSurvived: day,
        moneyEarned: money,
      };
      setIsRecordRunState(isRecordRun(currentStats));

      if (!loadStats.trainingMode) {
        const isNewBest = savePersonalBest(finalGameStats);
        if (isNewBest) {
          setPersonalBest(getPersonalBest());
        }
      }

      setAppState(AppState.GAME_OVER);
      GameAnalytics.trackGameOver({daysSurvived: day, training: loadStats.trainingMode});
    }
  }, [rating, day, money, appState, loadStats, dailyStats]); // Add dailyStats.lostCustomers to dependencies

  // Check for Saved Game on Mount and Load
  useEffect(() => {
    const savedGame = loadGameState();
    if (savedGame && savedGame.dayComplete === true) {
      setDay(savedGame.day);
      setMoney(savedGame.money);
      setRating(savedGame.rating);
      setTrainingMode(savedGame.trainingMode);
      setLoadStats(savedGame);
      setDayClosed(savedGame.dayComplete);
    } else {
      clearGameState();
    }
  }, []);

  // Check for Baking Tooltip Seen and Fire Soft Onboarding
  useEffect(() => {
    const bakingTooltipSeen = localStorage.getItem('baking_tooltip_seen');
    
    if (!bakingTooltipSeen) {
      startNewDay('newGame');
      setTimerPaused(true);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once


  // Tooltips
  
  // Show baking tooltip when game starts
  useEffect(() => {
    if (appState === AppState.PLAYING) {
      showBakingTooltip();
    }
  }, [appState, showBakingTooltip]);

  // Show serving tooltip when first customer appears
  useEffect(() => {
    if (customers.length > 0 && !servingTooltip.visible && !servingTooltip.completed) {
      showServingTooltip();
    }
  }, [customers.length, servingTooltip, showServingTooltip]);

  // Show rating tooltip after first successful serve
  useEffect(() => {
    if (dailyStats.satisfiedCustomers === 1 && !ratingTooltip.visible && !ratingTooltip.completed) {
      showRatingTooltip();
      GameAnalytics.trackOnboarding();
    }
  }, [dailyStats.satisfiedCustomers, ratingTooltip, showRatingTooltip]);


  // Timer
  const handleTick = useCallback(() => {
    setTime(prevTime => prevTime + 1);
  }, []);

  // Callback when timer completes (optional)
  const handleComplete = useCallback(() => {
    // Handle any completion logic if necessary
  }, []);

  useTimer({
    isActive: appState === AppState.PLAYING && !dayClosed && !timerPaused,
    onTick: handleTick,
    onComplete: handleComplete,
    interval: 1000, // 1 second
  });

// Scroll-Lock
  useEffect(() => {
    toggleScrollLock(appState === AppState.PLAYING);
    return () => toggleScrollLock(false);
  }, [appState]);
  
  return (
    <>
      <GoatCounter siteCode="bakerush" />
      <div className="min-h-[100dvh] flex flex-col">
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          sensors={sensors}
          modifiers={[restrictToWindowEdges]}
        >
          <div className="flex-grow">
            <Card className="w-full max-w-2xl mx-auto sm:mt-5 relative sm:border sm:rounded-lg border-0 rounded-none shadow-none sm:shadow-sm">
              
              
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                <div className="flex flex-col">
                  <span>{t('game.title')}</span>
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    {(() => {
                      return (
                        <>
                          {trainingMode && (
                            <span className="text-yellow-500">
                              {t('game.training')}
                            </span>
                          )}
                          {t('game.day', { day: day })}
                        </>
                      );
                    })()}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="flex items-center gap-2">💰 {t('game.currency', {amount: money})}</span>
                  <span className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={i < Math.round(rating / 10) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"} size={16} />
                    ))}
                    <span className="text-sm text-gray-500">{(rating / 10).toFixed(1)}</span>
                  </span>
                </div>
                </CardTitle>
              </CardHeader>


              <CardContent>
                <div className="space-y-2 sm:space-y-6">

                {(() => {
                  switch (appState) {
                    case AppState.GAME_OVER: // Game Over
                      return (
                        <div className="text-center p-6 sm:p-8 bg-stone-100 rounded-lg">
                          <h3 className="font-bold text-xl mb-3">
                            {isRecordRunState 
                              ? (trainingMode 
                                  ? t('game.gameOver.recordTitleTraining')
                                  : t('game.gameOver.recordTitle'))
                              : (trainingMode
                                  ? t('game.gameOver.titleTraining')
                                  : t('game.gameOver.title'))}
                          </h3>
                          <p className="mb-4 sm:mb-8">
                            <TranslatedText content={
                              isRecordRunState
                                ? (trainingMode 
                                    ? t('game.gameOver.recordSubtitleTraining')
                                    : t('game.gameOver.recordSubtitle'))
                                : (trainingMode
                                    ? t('game.gameOver.subtitleTraining') 
                                    : t('game.gameOver.subtitle'))
                            } />
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-left mb-8">
                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <h4 className="font-semibold mb-2">{t('game.gameOver.stats')}</h4>
                              <div className="space-y-1">
                                <div className="grid" style={{ gridTemplateColumns: 'minmax(30px, 1fr) minmax(75px, auto)' }}>
                                    <div>📅 {t('game.gameOver.successfulDays')}:</div>
                                    <div className="text-right font-medium">{finalStats.daysSurvived}</div>
                                    <div>👥 {t('game.gameOver.customersServed')}:</div>
                                    <div className="text-right font-medium">{finalStats.servedCustomers}</div>
                                  </div>
                              </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <h4 className="font-semibold mb-2">{t('game.gameOver.financialResults')}</h4>
                              <div className="space-y-1">
                                <div className="grid" style={{ gridTemplateColumns: 'minmax(30px, 1fr) minmax(75px, auto)' }}>
                                    <div>💵 {t('game.gameOver.totalRevenue')}:</div>
                                    <div className="text-right font-medium">{t('game.currency', {amount: finalStats.totalRevenue})}</div>
                                    <div>💰 {t('game.gameOver.moneyEarned')}:</div>
                                    <div className="text-right font-medium">{t('game.currency', {amount: finalStats.moneyEarned})}</div>
                                  </div>
                              </div>
                            </div>
                          </div>

                          <div className="mb-8 sm:mb-10 relative">
                            <Highscores refreshTrigger={scoreRefreshTrigger} className={"max-h-40 sm:max-h-60"} />
                            { submissionStatus && (
                              <SubmissionOverlay
                                status={submissionStatus}
                                message={
                                  submissionStatus === 'success' ? t('game.gameOver.scoreSubmitted') :
                                  submissionStatus === 'score-updated' ? t('game.gameOver.scoreUpdated') :
                                  submissionStatus === 'lower-score' ? t('game.gameOver.scoreTooLow') :
                                  t('game.gameOver.submitError')
                                }
                              />
                            )}
                            
                            { !submissionSuccess && !trainingMode && ( 
                              <div className="mt-3">
                                <div className="flex justify-center gap-2">
                                  <input
                                    type="text"
                                    value={playerName}
                                    onChange={(e) => setPlayerName(e.target.value)}
                                    placeholder={t('game.gameOver.enterName')}  
                                    disabled={submissionInProgress || submissionStatus === "error" || submissionStatus === "lower-score"}
                                    maxLength={30}
                                    className="px-4 py-2 rounded-lg border w-full max-w-xs"
                                  />
                                  <Button
                                    onClick={handleSubmitScore}
                                    disabled={!playerName.trim() || submissionInProgress || submissionStatus === "error" || submissionStatus === "lower-score"}
                                    variant="outline"
                                    className="border-stone-400 text-stone-800 hover:bg-stone-50 leading-none"
                                  >
                                    {submissionInProgress ? '...' : '✓'}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>


                          
                          <div className="space-y-2 mb-6 sm:mb-10">
                            <div className="flex flex-col sm:flex-row gap-4 sm:gap-2 justify-center items-center">
                              { day > 1 && (
                                <Button
                                  onClick={() => {
                                    GameAnalytics.trackRetry({daysSurvived: finalStats.daysSurvived});  
                                    startNewDay("retryInTrainingMode");
                                    }
                                  }
                                  className="border-stone-800 text-stone-800 hover:bg-stone-50 leading-none"
                                  variant="outline"
                                >
                                  {t('game.gameOver.trainingMode')}
                                </Button>
                              )}
                              <Button
                                onClick={() => {
                                    GameAnalytics.trackRestart({daysSurvived: finalStats.daysSurvived});
                                    startNewDay("newGame");
                                  }
                                }
                                className="bg-stone-800 hover:bg-stone-900 text-white transition-colors leading-none"
                              >
                                {t('game.gameOver.openNew')}
                                <ChevronRight className="w-6 h-6" />
                              </Button>
                            </div>
                            <div className="flex justify-center gap-2">
                              <HomeButton
                                    className=""
                                    onClick={() => backToHome("game-over")} // Pass the source parameter
                                  />
                              <ShareButton
                                  message={t('game.shareButton.finalStats', {
                                    days: finalStats.daysSurvived,
                                    customers: finalStats.servedCustomers
                                  })}
                                  source="game-over"
                                  className=""
                                />
                            </div>
                          </div>
                        </div>
                      )

                    case AppState.DAY_BREAK: // Day Break Screen
                      return (   
                        <div className="text-center sm:p-8 p-6  bg-stone-100 rounded-lg">
                          <h3 className="font-bold text-xl mb-3">{t('game.endOfDay.title', { day })}</h3>
                          <p className="mb-4 sm:mb-8">
                            {isRecordRunState && day !== 1 ? (
                               trainingMode 
                               ? ( t('game.endOfDay.recordRunTraining') )
                               : ( t('game.endOfDay.recordRun') )
                              ) : (
                              (() => {
                                const key = day === 1 ? 'day1'
                                  : day === 2 ? 'day2'
                                  : day === 3 ? 'day3'
                                  : day === 4 ? 'day4'
                                  : day === 5 ? 'day5'
                                  : day === 6 ? 'day6'
                                  : day === 7 ? 'day7'
                                  : day === 8 ? 'day8'
                                  : day === 9 ? 'day9'
                                  : day === 10 ? 'day10'
                                  : day === 20 ? 'day20'
                                  : day === 50 ? 'day50'
                                  : day === 100 ? 'day100'
                                  : 'default';

                                return t(`game.endOfDay.${key}`);
                              })()
                            )}
                          </p>

                          <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4 gap-2 text-left mb-6 sm:mb-8">
                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <h4 className="font-semibold mb-2">{t('game.customerService')}</h4>
                              <div className="space-y-1">
                              <div className="grid" style={{ gridTemplateColumns: 'minmax(30px, 1fr) minmax(75px, auto)' }}>
                                <div>👥 {t('game.satisfiedCustomers')}:</div>
                                <div className="text-right font-medium">{dailyStats.satisfiedCustomers}</div>
                                
                                <div>⌛ {t('game.lostCustomers')}:</div>
                                <div className="text-right font-medium">{dailyStats.lostCustomers}</div>
                                
                                <div className="flex items-center gap-1"><Star className="fill-yellow-400 text-yellow-400" size={16} /><span className="pl-2 sm:pl-0">{t('game.rating')}:</span></div>
                                <div className={`text-right font-medium ${dailyStats.ratingChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {dailyStats.ratingChange >= 0 ? '+' : ''}{dailyStats.ratingChange.toFixed(1)}
                                </div>
                              </div>
                              </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <h4 className="font-semibold mb-2">{t('game.finances')}</h4>
                              <div className="space-y-1">
                                <div className="grid" style={{ gridTemplateColumns: 'minmax(30px, 1fr) minmax(75px, auto)' }}>
                                  <div>📈 {t('game.revenue')}:</div>
                                  <div className="text-right font-medium">{t('game.currency', {amount: dailyStats.revenue})}</div>
                                  
                                  <div>📉 {t('game.expenses')}:</div>
                                  <div className="text-right font-medium">{t('game.currency', {amount: dailyStats.productionCosts})}</div>
                                  
                                  <div>💰 {t('game.profit')}:</div>
                                  <div className={`text-right font-medium ${(dailyStats.revenue - dailyStats.productionCosts) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {(dailyStats.revenue - dailyStats.productionCosts) >= 0 ? (
                                      t('game.currencyPositive', {amount: Math.abs(dailyStats.revenue - dailyStats.productionCosts)})
                                    ) : (
                                      t('game.currencyNegative', {amount: Math.abs(dailyStats.revenue - dailyStats.productionCosts)})
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <h4 className="font-semibold mb-2">{t('game.productsSold')}</h4>
                              <div className="space-y-1">
                                <div className="grid" style={{ gridTemplateColumns: 'minmax(30px, 1fr) minmax(75px, auto)' }}>
                                  <div>🥖 {t('game.breads')}:</div>
                                  <div className="text-right font-medium">{dailyStats.soldProducts.brot}</div>
                                  
                                  <div>🥨 {t('game.pretzels')}:</div>
                                  <div className="text-right font-medium">{dailyStats.soldProducts.breze}</div>
                                  
                                  <div>🎂 {t('game.cakes')}:</div>
                                  <div className="text-right font-medium">{dailyStats.soldProducts.kuchen}</div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow-sm">
                              <h4 className="font-semibold mb-2">{t('game.productsWasted')}</h4>
                              <div className="space-y-1">
                              <div className="grid" style={{ gridTemplateColumns: 'minmax(30px, 1fr) minmax(75px, auto)' }}>
                                <div>🥖 {t('game.breads')}:</div>
                                <div className="text-right font-medium">{dailyStats.wastedProducts.brot}</div>
                                
                                <div>🥨 {t('game.pretzels')}:</div>
                                <div className="text-right font-medium">{dailyStats.wastedProducts.breze}</div>
                                
                                <div>🎂 {t('game.cakes')}:</div>
                                <div className="text-right font-medium">{dailyStats.wastedProducts.kuchen}</div>
                              </div>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex gap-2 justify-center items-center">
                              {(() => { 
                                if (trainingMode) {
                                  return (
                                    <Button
                                      onClick={() => startNewDay('continue')}
                                      className="bg-stone-800 hover:bg-stone-900 text-white transition-colors leading-none"
                                    >
                                      {t('game.continueTraining')}
                                      <ChevronRight className="w-6 h-6" />
                                    </Button>
                                  );
                                }
                                else {
                                  return (
                                    <Button
                                      onClick={() => startNewDay('continue')}
                                      className="bg-stone-800 hover:bg-stone-900 text-white transition-colors leading-none"
                                    >
                                      {t('game.startDay', { day: day + 1 })}
                                      <ChevronRight className="w-6 h-6" />
                                    </Button>
                                  );
                                }
                              })()}
                            </div>
                            <div className="flex gap-2 justify-center items-center">
                              <HomeButton
                                    className=""
                                    onClick={() => backToHome("day-break")} // Pass the source parameter
                                    />
                              <ShareButton
                                    message={t('game.shareButton.dayBreakStats', {
                                      days: day,
                                      customers: dailyStats.satisfiedCustomers,
                                      revenue: dailyStats.revenue
                                    })}
                                    source="day-break"
                                    className=""
                                  />
                            </div>
                          </div>
                        </div>
                      )

                    case AppState.PLAYING: // Game Screen
                      return (
                        <>
                          <div className="text-center p-2 rounded-lg bg-stone-100 px-4 py-3 border border-stone-200 relative overflow-hidden">
                            <div className="flex items-center justify-between">
                              <div className="invisible w-4">⌛</div> {/* Spacer for alignment */}
                              <div>
                                <div className="font-bold">{dt}</div>
                                <div className="text-xs sm:text-sm">
                                  {!isOpen && !isBaking && t('game.closed')}
                                  {!isOpen && isBaking && t('game.bakingTime')}
                                  {isOpen && t('game.openForBusiness')}
                                </div>
                              </div>
                              <CancelButton 
                                  onClick={() => setCancelModalConfig({
                                    isOpen: true,
                                    onConfirm: () => {backToHome("in-game-cancel")
                                    }
                                  })}
                                />
                            </div>
                            
                            {/* Progress bar */}
                            <div 
                              className={`absolute bottom-0 left-0 h-1 
                              ${
                                time % SPD >= 4 * (SPD / 24) && time % SPD < 6 * (SPD / 24)
                                  ? 'bg-stone-300'
                                  : time % SPD >= 6 * (SPD / 24) && time % SPD <= 18 * (SPD / 24)
                                  ? 'bg-stone-400'
                                  : 'bg-stone-800'
                              }  
                            `}
                              style={{
                                width: (() => {
                                  const startTime = 4 * (SPD/24); // 4:00
                                  const endTime = 18 * (SPD/24); // 18:00
                                  const current = time % SPD;
                                  const progress = Math.max(0, Math.min(1, (current - startTime) / (endTime - startTime)));
                                  return `${progress * 100}%`;
                                })(),
                                transition: 'width 1s linear'
                              }}
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="border border-stone-200 rounded-lg px-4 py-3 sm:py-4 bg-white transition-colors">
                              <h3 className={`font-bold mb-2 ${day > 1 ? 'sm:block hidden' : ''}`}>
                                {t('customers.waiting', { count: customers.length })}
                              </h3>
                              <div className={`flex flex-wrap items-start gap-1 sm:gap-2 overflow-hidden`}>
                                {(() => {
                                  const maxQueueSize = getMaxQueueSize(day);
                                  return Array.from({ length: maxQueueSize }, (_, index) => {
                                    const customer = customers.find(c => c.position === index);

                                    if (customer) {
                                      return (
                                        <DroppableCustomer
                                          key={customer.id}
                                          customer={customer}
                                          onServe={handleServeCustomer}
                                          productData={PRODUCTS}
                                          currentStock={products}
                                          earned={customer.earned}
                                          ratingLost={customer.ratingLost}
                                        />
                                      );
                                    }

                                    return (
                                      <div
                                        key={`placeholder-${index}`}
                                        className="w-14 sm:w-16 h-[36px] sm:h-[40px] rounded bg-stone-50/50 border border-dashed border-stone-200"
                                      />
                                    );
                                  });
                                })()}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2 sm:space-y-0">
                            {/* Products + Oven Container */}
                            <div className="grid grid-cols-3 gap-2 sm:gap-4 select-none">
                              {Object.entries(PRODUCTS).map(([p, i]) => (
                                <div key={p} className="space-y-2">
                                  {/* Product Card */}
                                  <Card className="px-2 sm:px-4 py-2 sm:py-3 border-stone-200 border shadow-sm transition-colors">
                                    <div className="flex flex-col gap-y-2 sm:gap-y-4">
                                      <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2">
                                        <DraggableProduct
                                          type={p}
                                          amount={products[p].amount}
                                          emoji={i.e}
                                          isDragging={p === activeId}
                                        />
                                        <div className="w-full text-xs sm:text-sm text-center sm:text-left">
                                          <div>{t('game.inStock')} {products[p].amount}</div>
                                          <div>{t('game.inOven')} {products[p].inProduction * i.b}/{PRODUCTS[p].o * i.b}</div>
                                        </div>
                                      </div>

                                      <Button
                                        onClick={() => startProduction(p)}
                                        disabled={money < i.c || !isBaking || products[p].inProduction >= PRODUCTS[p].o}
                                        className="w-full min-h-[40px] sm:min-h-0 py-1 sm:py-2 text-xs sm:text-sm bg-stone-800 hover:bg-stone-900 text-white transition-colors select-none leading-tight sm:leading-none"
                                      >
                                        <span className="hidden sm:inline">
                                          {t('game.productionCostFull', { count: i.b, cost: i.c })}
                                        </span>
                                        <span className="sm:hidden">
                                          {t('game.productionCost', { count: i.b })}
                                          <br />
                                          <span className="font-normal">({t('game.currency', { amount: i.c })})</span>
                                        </span>
                                      </Button>

                                      {/* Oven Section */}
                                      <div className="pt-2 sm:pt-4 border-t border-stone-200 min-h-9 sm:min-h-[5rem]">
                                        {products[p].productionTimes.length > 0 && (
                                          <div className="flex flex-wrap gap-1 justify-center">
                                            {products[p].productionTimes.map((time, idx) => (
                                              <span
                                                key={idx}
                                                className={`
                                                inline-flex h-6 sm:h-7 items-center justify-center 
                                                bg-stone-100 rounded px-1 py-1 text-xs sm:text-sm
                                                ${i.b > 1 ? 'min-w-[45px] sm:min-w-[60px]' : 'min-w-[30px] sm:min-w-[40px]'}
                                              `}
                                              >
                                                {time}s {i.b > 1 && <small className="ml-1 text-stone-500">{i.b}x</small>}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </Card>
                                </div>
                              ))}
                            </div>
                          </div>


                          {/* Add tooltips */}
                          <Tooltip
                            visible={bakingTooltip.visible}
                            onDismiss={dismissBakingTooltip}
                            position="sm:top-[427px] top-[190px] sm:left-24 right-4"
                            showDismissButton={false}
                          >
                            <TranslatedText content={t('game.tooltips.baking')} />
                          </Tooltip>

                          <Tooltip
                            visible={servingTooltip.visible}
                            onDismiss={dismissServingTooltip}
                            position="sm:top-[180px] top-[80px] right-4"
                            showDismissButton={false}
                          >
                            <TranslatedText content={t('game.tooltips.serving')} />
                          </Tooltip>

                          <Tooltip
                            visible={ratingTooltip.visible}
                            onDismiss={dismissRatingTooltip}
                            position="top-[60px] sm:top-[50px] right-4"
                            showDismissButton={false}
                          >
                            <TranslatedText content={t('game.tooltips.rating')} />
                          </Tooltip>
                        </>
                      )
                    
                    default: // entspricht case AppState.WELCOME: 
                      return (                                
                        <div className="text-center sm:p-8 p-6 bg-stone-100 rounded-lg">
                          <h2 className="text-xl font-bold mb-3">{t('game.welcome.title')}</h2>
                          <div className="sm:mb-8 mb-6">
                            <p>{t('game.welcome.subtitle')}</p>
                          </div>

                          {personalBest ? (
                            <PersonalBestDisplay personalBest={personalBest} />
                          ) : (
                            <>
                              <div className="hidden sm:block sm:mb-8 mb-6">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2">
                                  <div className="bg-white p-4 rounded-lg shadow-sm">
                                    <span className="text-3xl block mb-2">🥨</span>
                                    <p className="text-sm text-stone-600">{t('game.welcome.features.baking')}</p>
                                  </div>
                                  <div className="bg-white p-4 rounded-lg shadow-sm">
                                    <div className="flex justify-center mb-2">
                                      <Star className="fill-yellow-400 text-yellow-400" size={36} />
                                    </div>
                                    <p className="text-sm text-stone-600">{t('game.welcome.features.customers')}</p>
                                  </div>
                                  <div className="bg-white p-4 rounded-lg shadow-sm">
                                    <span className="text-3xl block mb-2">💰</span>
                                    <p className="text-sm text-stone-600">{t('game.welcome.features.profit')}</p>
                                  </div>
                                </div>
                              </div>

                              <div className="sm:hidden mb-2 p-2 rounded-lg">
                                <p>
                                  <span className="text-4xl">🥖 </span>
                                  <span className="text-4xl">🥨 </span>
                                  <span className="text-4xl">🎂</span>
                                </p>
                              </div>
                            </>
                          )}

                          <div className="mb-10">
                            <Highscores refreshTrigger={scoreRefreshTrigger} className="max-h-40 sm:max-h-44"/>
                          </div>

                          <div className="mb-4">
                            <div className="flex flex-col sm:flex-row gap-4 sm:gap-2 justify-center items-center">
                              {(() => { 
                                if (dayClosed && !trainingMode) {
                                  return (
                                    <>
                                      <Button
                                        onClick={() => setCancelModalConfig({
                                            isOpen: true, 
                                            onConfirm: () => startNewDay("newGame")
                                          })}
                                        variant="outline"
                                        className="border-stone-800 text-stone-800 hover:bg-stone-50 leading-none w-fit"
                                      >
                                        {t('game.welcome.newBakery')}
                                      </Button>
                                      <Button
                                        onClick={() => startNewDay('continue')}
                                        className="bg-stone-800 hover:bg-stone-900 text-white transition-colors leading-none w-fit"
                                      >
                                        {t('game.welcome.continue', { day: day + 1 })}
                                        <ChevronRight className="w-6 h-6" />
                                      </Button>
                                    </>
                                  ); }
                                  if (dayClosed && trainingMode) {
                                    return (
                                      <>
                                        <Button
                                          onClick={() => {
                                            startNewDay("newGame");
                                          }}
                                          variant="outline"
                                          className="border-stone-800 text-stone-800 hover:bg-stone-50 leading-none w-fit"
                                        >
                                          {t('game.welcome.newBakery')}
                                        </Button>
                                        <Button
                                          onClick={() => startNewDay('continue')}
                                          className="bg-stone-800 hover:bg-stone-900 text-white transition-colors leading-none w-fit"
                                        >
                                          {t('game.welcome.continueTraining', { day: day + 1 })}
                                          <ChevronRight className="w-6 h-6" />
                                        </Button>
                                      </>
                                    ); }                                  
                                else {
                                  return (
                                    <Button
                                      onClick={() => startNewDay('newGame')}
                                      className="
                                        bg-stone-800 hover:bg-stone-900
                                        text-white font-medium sm:text-lg text-base
                                        px-8 py-4
                                        rounded-lg
                                        border border-stone-700
                                        relative overflow-hidden
                                        tracking-wide
                                        transition-all duration-600
                                        hover:[box-shadow:0_4px_14px_0_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.05)_inset]
                                      "
                                    > 
                                      {t('game.welcome.openBakery')} 
                                      <ChevronRight className="w-6 h-6" />
                                    </Button>
                                  );
                                }
                              })()}

                            </div>
                            <div className="flex gap-2 justify-center mt-2">
                              <TrainingButton
                                onClick={() => setIsTrainingModalOpen(true)} />
                              <ShareButton
                                message={t('game.shareButton.neutral')}
                                source="landingpage-saved"
                                className="" />
                            </div>
                          </div>
                        </div>
                        )}
                    })()}
                </div>
              </CardContent>
            </Card>
          </div>

          <DragOverlay>
            {activeId ? <DragPreview emoji={PRODUCTS[activeId].e} className="touch-none" /> : null}
          </DragOverlay>
        </DndContext>

        <InstallAppModal 
          appState={appState} 
          day={day}
        />
        
        <CancelGameModal
          isOpen={cancelModalConfig.isOpen}
          onConfirm={() => {
            cancelModalConfig.onConfirm?.();
            setCancelModalConfig({ isOpen: false, onConfirm: null });
          }}
          onCancel={() => setCancelModalConfig({ isOpen: false, onConfirm: null })}
        />

        <TrainingModeModal 
          isOpen={isTrainingModalOpen}
          setIsOpen={setIsTrainingModalOpen}
          startNewDay={startNewDay}
        />


        { /* Footer */ }
        <footer className="w-full sm:py-8 pb-6 pt-4 mt-auto">
            <div className="w-full max-w-2xl mx-auto text-center">
            <div className="space-y-3 sm:text-sm text-xs text-stone-400">
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <a
                  href="https://www.maxwallner.com/imprint/"
                  className="hover:text-stone-600 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => GameAnalytics.trackFooterClick('imprint')}
                >
                  {t('footer.imprint')}
                </a>
                <span>•</span>
                <button
                  onClick={() => {
                    i18n.changeLanguage(i18n.language === 'en' ? 'de' : 'en');
                    GameAnalytics.trackFooterClick('language-switch');
                    }
                  }
                  className="bg-transparent hover:text-stone-600 transition-colors cursor-pointer"
                >
                  {i18n.language === 'en' ? 'Deutsch' : 'English'}
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BakeryGame;